import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { CartItem } from "../../types/types";
import { calculateDiscountedPrice } from "../../utils/format";
import { isPricedLine } from "../../utils/availability";

/**
 * What makes two cart lines the same line.
 *
 * This used to be the product **name**, and that is a real defect rather than a stylistic one: the
 * catalog-modernization work projected ~250 name collisions at full scale, which is why slugs were
 * rejected as row ids (`docs/catalog-modernization/README.md` §4.2.3). Two different products
 * called "Vidrio templado" merged into one line, at the first one's price, and the shopper was
 * charged for one of them twice.
 *
 * `ItemType.id` is optional, so the fallback stays — but it is namespaced, so an id-less line can
 * never collide with a real id, and the old behaviour survives only where there is genuinely
 * nothing better to key on.
 */
export const cartLineKey = (item: Pick<CartItem, "id" | "name">): string =>
  item.id ? `id:${item.id}` : `name:${item.name}`;

interface CartState {
  items: CartItem[];
  totalQuantity: number;
  /**
   * The money the shopper will actually be charged for products, before delivery.
   *
   * Named `itemsSubtotal` and not `totalPrice` on purpose: a cart holding a `QUOTE` line has no
   * total yet, and a field called "total price" that silently omits a third of the catalog is the
   * lie `docs/ordering-flow/README.md` §3.4 is about. Read it next to `quoteItemCount` or not at
   * all.
   */
  itemsSubtotal: number;
  /** How many lines have no price yet. Non-zero means every total on screen is partial. */
  quoteItemCount: number;
}

const initialState: CartState = {
  items: [],
  totalQuantity: 0,
  itemsSubtotal: 0,
  quoteItemCount: 0,
};

const recalcTotals = (state: CartState) => {
  state.totalQuantity = state.items.reduce((sum, item) => sum + item.quantity, 0);

  // `QUOTE` lines are excluded rather than added as zero. Arithmetically identical, and the
  // difference is the point: the count below is what lets the UI say the total is partial.
  state.itemsSubtotal = state.items
    .filter(isPricedLine)
    .reduce(
      (sum, item) => sum + calculateDiscountedPrice(item.price, item.discount) * item.quantity,
      0,
    );

  state.quoteItemCount = state.items.filter((item) => !isPricedLine(item)).length;
};

const cartSlice = createSlice({
  name: "cart",
  initialState,
  reducers: {
    addItemToCart: (state, action: PayloadAction<CartItem>) => {
      const key = cartLineKey(action.payload);
      const existingItem = state.items.find((item) => cartLineKey(item) === key);

      if (existingItem) {
        existingItem.quantity += action.payload.quantity;
        // Refresh the commercial facts from the copy the shopper just looked at. Price and stock
        // move under us — the weekly sync rewrites both — and the newer read is the better one.
        existingItem.price = action.payload.price;
        existingItem.discount = action.payload.discount;
        existingItem.available_quantity = action.payload.available_quantity;
        existingItem.lead_time_days = action.payload.lead_time_days;
      } else {
        state.items.push(action.payload);
      }

      recalcTotals(state);
    },

    removeItemFromCart: (state, action: PayloadAction<string>) => {
      const itemIndex = state.items.findIndex((item) => cartLineKey(item) === action.payload);
      if (itemIndex >= 0) {
        state.items.splice(itemIndex, 1);
        recalcTotals(state);
      }
    },

    updateCartItem: (state, action: PayloadAction<{ key: string; quantity: number }>) => {
      const itemIndex = state.items.findIndex((item) => cartLineKey(item) === action.payload.key);
      if (itemIndex < 0) return;

      // A line of zero is not a line. The minus button stops at 1 today, so this is defence
      // against a future caller rather than a live path — but a zero-quantity line renders as a
      // product in the cart contributing nothing, which reads as a pricing bug.
      if (action.payload.quantity <= 0) {
        state.items.splice(itemIndex, 1);
      } else {
        state.items[itemIndex].quantity = action.payload.quantity;
      }

      recalcTotals(state);
    },

    clearCart: (state) => {
      state.items = [];
      state.totalQuantity = 0;
      state.itemsSubtotal = 0;
      state.quoteItemCount = 0;
    },
  },
});

export const { addItemToCart, removeItemFromCart, updateCartItem, clearCart } = cartSlice.actions;
export default cartSlice.reducer;
